import { ExternalLinkIconInText } from "@client/icons/ExternalLinkIcon";
import { Field, FieldResponse } from "@engine/round/aggregate";
import { Anchorme, LinkComponentProps } from "react-anchorme";
import {
  alteredSectionHeaders,
  sectionHeadersToSkip,
  SpecialSectionType,
} from "./sectionUtils";

interface Props {
  field: Field;
  fieldResponse?: FieldResponse;
  descriptionHidden: boolean;
  toggleDescriptionVisibility: (id: string) => void;
  inSpecialSection: SpecialSectionType;
}

export function FieldContent({
  field,
  fieldResponse,
  descriptionHidden,
  toggleDescriptionVisibility,
  inSpecialSection,
}: Props) {
  if (field.status !== "active") return null;
  if (sectionHeadersToSkip.includes(field.title)) return null;

  const sectionHeader = alteredSectionHeaders[field.title] || field.title;

  if (field.type === "sectionHeader") {
    return (
      <h4 key={field.id} className="my-4">
        {sectionHeader}
      </h4>
    );
  }

  if (
    inSpecialSection === "longform" &&
    (!fieldResponse ||
      (fieldResponse.type === "question" && fieldResponse.value === ""))
  ) {
    return null;
  }

  const value =
    fieldResponse?.type === "question" ? fieldResponse.value : undefined;

  const content = value ? (
    <Anchorme linkComponent={Link}>{value}</Anchorme>
  ) : (
    "No response"
  );

  return (
    <div className="relative flex">
      <div className="ml-7">
        <div
          className="text-sm light:text-stone-700 dark:text-stone-300 cursor-pointer opacity-[0.9]"
          onClick={() => toggleDescriptionVisibility(field.id)}
        >
          {field.title}
        </div>
        {field.description && !descriptionHidden && (
          <div className="text-xs light:text-stone-700 dark:text-stone-300 italic mt-1 opacity-[0.65]">
            {field.description}
          </div>
        )}

        <div className="light:text-black dark:text-white text-sm pb-1 pl-2 mt-1 mb-6 border-l border-l-8 border-b border-l-stone-500 border-b-stone-700 break-words">
          {content}
        </div>
      </div>
    </div>
  );
}

function Link(props: LinkComponentProps) {
  return (
    <a
      href={props.href}
      className="light:text-blue-600 light:hover:text-blue-700 light:visited:text-violet-700 dark:text-blue-400 dark:hover:text-blue-300 dark:visited:text-violet-300"
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
      <ExternalLinkIconInText />
    </a>
  );
}
